/**
 * ENG-8215 — the one place the restart breaker and the MCP quarantine agree
 * on how many failures is too many.
 *
 * These two mechanisms were tuned in isolation and never against each other:
 *
 *   MCP auto-quarantine (ENG-7916)   20 consecutive bind failures
 *   Restart circuit breaker (ENG-5441/7560/7812)  5 provisioning restarts / 30min
 *
 * ENG-7916 exists specifically so a chronically-failing integration is isolated
 * INSTEAD of flapping the whole agent. It could never do that job, because the
 * breaker always tripped first and paused the agent. Observed live on sherlock
 * (demo-company, agt-demo-1) 2026-07-28: six provisioning restarts on one
 * integration, five of them `bind-remediation` — the manager's own repair
 * attempts — and the agent auto-paused with nobody having changed any config.
 *
 * The ~20h-vs-30min framing that made these look incomparable is only true when
 * the connectivity PROBE drives the counter. In the flapping case they count the
 * same events on the same clock: each bind-remediation respawn produces a
 * session-tool-bind report, which increments the failure counter 1:1 with the
 * restart. So a threshold below the breaker's bar genuinely does fire first.
 *
 * Both values live here, and the quarantine threshold is DERIVED, so tuning
 * either one cannot silently re-open the race. A test asserts the ordering.
 */

/**
 * Provisioning-reload restarts allowed within the provisioning window before the
 * breaker trips, counted PER INTEGRATION (ENG-7812). Source of truth: the host
 * breaker reads this, and the quarantine threshold is derived from it.
 */
export const RESTART_BREAKER_PROVISIONING_MAX = 5;

/** Sliding window for the provisioning tally. */
export const RESTART_BREAKER_PROVISIONING_WINDOW_MS = 1_800_000; // 30 min

/**
 * ENG-8388 — the separate, looser budget for a provisioning reload the platform
 * can PROVE a human asked for (`restart_actor: 'user'` on the host_agents stamp).
 *
 * The breaker exists to catch a LOOP. Six reloads produced by six deliberate
 * clicks is not a loop, but on the shared tally it is indistinguishable from one,
 * and that cost us real agents: a brand-new customer's first agent auto-paused
 * inside its first ten minutes for connecting four integrations (ENG-8204), and
 * `don` auto-paused on 2026-08-03 for six Webflow credential re-entries during
 * routine troubleshooting — the worst possible moment, since a paused agent binds
 * nothing and you cannot tell whether your fix worked.
 *
 * Why a bigger budget rather than the ENG-7541 / ENG-8215 "recorded but never
 * trips" treatment those two classes get: in both of those the PLATFORM is the
 * actor (a token re-mint, the manager's own repair), so a rate limit on them is
 * meaningless. Here the actor is a person, and the attribution arrives over the
 * wire from an API route. A buggy or looping caller that stamps `user` on an
 * autonomous reload would, under a blanket exemption, have no backstop at all.
 * A looser bar keeps one.
 *
 * 12 is 2x the worst burst actually observed (6 in 2.5 min, ENG-8204's audit
 * log), which is the headroom a genuine setup or debugging session needs, and it
 * still lands far below anything a real loop produces. Tunable per host via
 * `AGT_RESTART_BREAKER_OPERATOR_MAX`.
 *
 * NOTE this does NOT relax the autonomous tally: a storm with no user actor is
 * still counted by {@link RESTART_BREAKER_PROVISIONING_MAX}, unchanged.
 */
export const RESTART_BREAKER_OPERATOR_MAX = 12;

/**
 * ENG-9138 — how many PLATFORM-SCHEDULED restarts (the `scheduled` class; today
 * that is `day-rollover`) may land inside {@link RESTART_BREAKER_SCHEDULED_WINDOW_MS}
 * before the manager raises a loop signal.
 *
 * This is NOT a trip threshold, and the distinction is the whole ticket. The
 * scheduled class never pauses an agent (see the class comment in
 * restart-breaker.ts): a restart the platform itself scheduled is not evidence
 * the agent is unhealthy, and pausing on it means the platform's own maintenance
 * takes the customer's agent down. That is exactly what happened on 2026-08-18 —
 * `katie` (acquire-intelligence, a CUSTOMER agent) took three day-rollover
 * restarts inside ten minutes, crossed the tight crash bar of 2, and sat paused
 * for 8h16m with nobody paged.
 *
 * Crossing this number instead emits an `agent_pause_suppressed`-style report,
 * so a genuine rollover LOOP is still visible and still pages a human — it just
 * pages them about a platform bug rather than by killing the agent.
 *
 * Why 3. A correct day rollover produces exactly ONE restart per agent per day,
 * so any repeat inside the hour is already anomalous; 3 leaves room for a
 * legitimate retry plus a manual restart in the same hour without crying wolf,
 * and is the count katie actually reached, so the live incident is reproduced by
 * the threshold rather than argued around it.
 */
export const RESTART_BREAKER_SCHEDULED_MAX = 3;

/**
 * ENG-9138 — sliding window for the scheduled tally. One hour, not the tight
 * 10-minute crash window: a rollover loop that respawns every 20 minutes is
 * still a loop, and a window shorter than the thing it is watching for cannot
 * see it. Deliberately longer than both other windows, which is why the breaker
 * widens its event retention to cover it.
 */
export const RESTART_BREAKER_SCHEDULED_WINDOW_MS = 3_600_000; // 60 min

/**
 * ENG-10066 — how many OPERATOR-COMMANDED restarts (the `operator-command`
 * class; today that is a human typing `/restart` in Slack or Telegram) may land
 * inside {@link RESTART_BREAKER_OPERATOR_COMMAND_WINDOW_MS} before the manager
 * raises a loop signal.
 *
 * Like {@link RESTART_BREAKER_SCHEDULED_MAX}, this is NOT a trip threshold, and
 * for the same reason one layer along: a restart a human deliberately asked for
 * is not evidence the agent is unhealthy, and pausing on it takes away the one
 * tool the person recovering the agent has. Before this class existed, a channel
 * `/restart` fell through to the tight crash bar of 2 — so the THIRD `/restart`
 * inside ten minutes auto-paused the very agent the operator was trying to
 * revive.
 *
 * That is not hypothetical. razorclaw-sales (acquire-intelligence — the same
 * org as katie above, and the same shape of incident) took 21 channel `/restart`
 * restarts on 2026-09-05 in seven clusters of exactly three. Every cluster
 * stopped at three because the third one tripped the breaker, and each of the
 * first six pauses had to be cleared by a human clicking resume. The seventh
 * landed at 23:53Z, nobody was left awake to clear it, and the agent stayed down
 * for over fourteen hours on a completely healthy host.
 *
 * Why 5. A person recovering an agent legitimately restarts it a few times in a
 * sitting — the observed human bursts run to three at a time — so 5 clears
 * ordinary recovery without crying wolf, while razorclaw's real burst (nine
 * restarts inside eighteen minutes) crosses it comfortably and gets reported.
 * Tunable per host via `AGT_RESTART_BREAKER_OPERATOR_COMMAND_MAX`.
 */
export const RESTART_BREAKER_OPERATOR_COMMAND_MAX = 5;

/**
 * ENG-10066 — sliding window for the operator-command tally. One hour, matching
 * the scheduled window: what makes a run of operator restarts worth reporting is
 * that somebody kept needing them, and a 10-minute window cannot see a person
 * who restarts, waits, checks, and restarts again.
 */
export const RESTART_BREAKER_OPERATOR_COMMAND_WINDOW_MS = 3_600_000; // 60 min

/**
 * ENG-8690 — how long after an agent is created its PROVISIONING churn cannot
 * pause it. Crash-class restarts are unaffected; see below.
 *
 * WHY A THIRD MECHANISM, when three already exist.
 *
 * The breaker's premise is that repeated .mcp.json membership churn on ONE
 * integration is evidence of a fault. That premise is simply false for a
 * brand-new agent: during onboarding, membership churn is the EXPECTED shape of
 * a human wiring integrations up one after another. The classification is
 * correct and the interpretation is wrong, which is why none of the existing
 * escape hatches catch it:
 *
 *   - `credential-rotation` (ENG-7541) is emitted ONLY when every changed
 *     env-only var is a value-only rotation of an ALREADY-PRESENT var. In the
 *     first hours vars appear for the FIRST time, so the delta is a membership
 *     change and it correctly stays 'hot-reload-mcp'.
 *   - `self-healing` (ENG-8215) covers the manager's own repair attempts, not
 *     provisioning churn driven from outside.
 *   - the operator budget (ENG-8388) needs `restart_actor: 'user'`. A managed
 *     integration re-minting its own token has no human actor, so it falls back
 *     to the tight autonomous tally.
 *
 * The live case: Acquire Intelligence's first agent auto-paused NINE MINUTES
 * after activation, when one Composio integration rotated its managed-state
 * token six times in thirty minutes and each rotation landed in the same
 * per-integration provisioning bucket. ENG-8204 was the same shape (a new
 * customer's first agent paused inside ten minutes for connecting four
 * integrations) and was patched with the operator budget, which is precisely why
 * this recurred: that patch only covers the case a human can be proven behind.
 *
 * The cost of the failure is asymmetric and front-loaded. A paused agent binds
 * nothing, so the customer's very first impression is an agent that does not
 * work, at the one moment nobody has the context to tell a real fault from
 * onboarding noise.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It does not touch the crash tally. A
 * brand-new agent that crash-loops still trips on the tight ENG-5441 bar, and a
 * test pins that. Suppressing crash trips for a day would let a structurally
 * broken agent respawn for 24 hours - the failure this whole subsystem exists
 * to stop - and "it is new" is a reason to expect CONFIG churn, not a reason to
 * expect segfaults.
 *
 * 24 hours is Brad's directive off the Acquire incident, not a tuned number. It
 * wants to comfortably cover a first working day in any timezone: integrations
 * get connected across a morning, someone picks it up again after lunch, and a
 * token rotation overnight must not undo it. Tunable per host via
 * `AGT_RESTART_BREAKER_NEW_AGENT_GRACE_MS`; set it to 0 to disable the grace.
 */
export const RESTART_BREAKER_NEW_AGENT_GRACE_MS = 86_400_000; // 24 hours

/**
 * The ceiling the new-agent grace does NOT lift: provisioning-shaped restarts in
 * the provisioning window that will pause even a brand-new agent.
 *
 * The grace as first written was UNBOUNDED inside its 24 hours. That is a real
 * hole and it was raised in review: an agent thrashing every few seconds would
 * have respawned for a day with nothing to stop it. The crash carve-out does not
 * cover this, because a provisioning loop is not a crash - the agent comes up
 * fine each time and immediately reloads again.
 *
 * 30-in-30-minutes is 6x the normal per-integration bar, which is the point:
 * onboarding a lot of integrations quickly should never approach it, while a
 * genuine loop crosses it in minutes. Note it is counted across ALL buckets
 * rather than per-integration, unlike the normal provisioning trip - during the
 * grace the question is not "is one integration churning" (that is expected)
 * but "is this agent doing nothing except restarting".
 *
 * A trip here is reported distinctly, naming the ceiling, so an operator can
 * tell it from an ordinary provisioning pause: it means the grace was in force
 * and was not enough, which is a materially different thing to investigate.
 *
 * Tunable via `AGT_RESTART_BREAKER_NEW_AGENT_GRACE_CEILING`.
 *
 * PROVENANCE, stated because it affects how much to trust the number: review
 * cited this as an ENG-8679 acceptance criterion ("suggested: 30 restarts in 30
 * minutes"). I have no Linear access from this session and could NOT verify that
 * ticket's text. The ceiling is implemented because it is correct on its own
 * merits - an unbounded grace is a hole regardless of who wrote it down - and
 * the specific value should be treated as a reviewer's suggestion I adopted, not
 * as a verified requirement.
 */
export const RESTART_BREAKER_NEW_AGENT_GRACE_CEILING = 30;

/**
 * How many consecutive bind failures quarantine an integration.
 *
 * Derived, deliberately, rather than picked: it must land strictly below
 * {@link RESTART_BREAKER_PROVISIONING_MAX} so quarantine pre-empts the trip it
 * was built to pre-empt. The -2 is headroom, not superstition — the breaker
 * trips on the count EXCEEDING its max, and a bind loop can emit a restart the
 * counter has not yet seen, so leaving a single slot would make the ordering a
 * coin toss on interleaving.
 *
 * Floored at 2 so a future reduction of the breaker max can never derive a
 * threshold of 1 (or 0), which would quarantine an integration on a single
 * transient blip — the failure mode ENG-7575 exists to avoid.
 */
export const BIND_FAILURE_QUARANTINE_THRESHOLD = deriveBindFailureQuarantineThreshold(
  RESTART_BREAKER_PROVISIONING_MAX,
);

/**
 * The derivation, exposed as a function so the ordering survives RUNTIME tuning
 * and not just the compiled defaults (CodeRabbit on PR #3854).
 *
 * The constants above only coordinate the two DEFAULTS. The host breaker also
 * accepts `AGT_RESTART_BREAKER_PROVISIONING_MAX` and a programmatic
 * `opts.provisioningMax`, either of which could lower the breaker's bar on its
 * own — an operator setting `2` would trip on the third provisioning restart
 * while quarantine sat waiting for its (default-derived) three, silently
 * re-opening the exact race this ticket closes. The quarantine threshold is
 * server-side and cannot see a host's env, so the two cannot be re-derived
 * together at runtime; the ordering has to be enforced on the host instead.
 */
export function deriveBindFailureQuarantineThreshold(provisioningMax: number): number {
  return Math.max(2, provisioningMax - 2);
}

/**
 * The lowest provisioning max that still lets quarantine fire first, given a
 * quarantine threshold the host cannot change.
 *
 * `BIND_FAILURE_QUARANTINE_THRESHOLD` is enforced by the API (ENG-7916), so a
 * host-side override can only move the breaker. This is the floor the breaker
 * must respect for the ordering invariant to hold: the breaker trips on a count
 * strictly EXCEEDING its max, so a max of `threshold + 1` means quarantine's
 * Nth failure lands before the breaker's (N+2)th restart.
 *
 * Clamping rather than throwing is deliberate. A manager that refuses to start
 * on a fat-fingered env var is a worse outcome than one that runs with a
 * slightly looser breaker: the breaker is a safety net, and the ordering
 * invariant is the thing being protected. The clamp is logged by the caller so
 * the override is not silently discarded.
 */
export const MIN_PROVISIONING_MAX_FOR_QUARANTINE_ORDERING = BIND_FAILURE_QUARANTINE_THRESHOLD + 1;
